import React, { useState } from 'react';
import styles from './ItemConfigWizard.module.css';

const formatCurrency = value => {
  if (!value) return '';
  const num = parseFloat(value);
  if (isNaN(num)) return '';
  return new Intl.NumberFormat('he-IL', {
    style: 'currency',
    currency: 'ILS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

/**
 * ItemConfigForm - Shared form component for configuring item options
 *
 * Used by:
 * - ItemConfigWizard (for adding new items)
 * - ItemOptionsEditor (for editing existing items)
 *
 * Props:
 * - item: The master item being configured
 * - config: Current configuration state { quantity, cost_override, selected_options, cost_type_id, incl_vat }
 * - onChange: Callback when any field changes - receives (fieldName, value)
 * - costTypes: Array of cost types for dropdown
 * - showVat: Whether to show VAT checkbox (default true)
 * - showQuantity: Whether to show quantity field (default true)
 * - showCostType: Whether to show cost type dropdown (default: only if item has no cost_type_id)
 * - masterSpaceId: Optional — filters item_options.options[] by allowed_space_ids. If omitted, all options shown.
 */
function ItemConfigForm({
  item,
  config = {},
  onChange,
  costTypes = [],
  showVat = true,
  showQuantity = true,
  showCostType,
  masterSpaceId,
}) {
  // Price estimate is collapsed by default; user clicks "+ הוספת הערכת מחיר" to reveal.
  // If a cost_override already exists (edit flow on an item with a price), start expanded.
  const [priceExpanded, setPriceExpanded] = useState(() => config.cost_override != null);

  if (!item) return null;

  // Determine if we should show cost type dropdown
  const shouldShowCostType = showCostType ?? (!item.cost_type_id && costTypes.length > 0);

  // Resolve the current cost type name (for the read-only badge when the item has a preset)
  const currentCostTypeName = item.cost_type_id
    ? costTypes.find(ct => ct.id === item.cost_type_id)?.name || null
    : null;

  // Group options by their group field (or 'default' if no group)
  // Also filter by masterSpaceId (when provided) so options like "dining chair"
  // don't show when the user is picking a chair for a bedroom.
  const groupedOptions = React.useMemo(() => {
    if (!item.item_options?.options) return {};

    const selectedOptions = config.selected_options || {};
    const visibleOptions = item.item_options.options.filter(opt => {
      if (!masterSpaceId) return true;
      const allowed = opt.allowed_space_ids;
      if (!Array.isArray(allowed) || allowed.length === 0) return true;
      if (allowed.includes(masterSpaceId)) return true;
      // Preserve currently-selected option to avoid hiding committed data on edit
      const group = opt.group || 'default';
      if (selectedOptions[group] === opt.name) return true;
      return false;
    });

    return visibleOptions.reduce((acc, opt) => {
      const group = opt.group || 'default';
      if (!acc[group]) acc[group] = [];
      acc[group].push(opt);
      return acc;
    }, {});
  }, [item.item_options?.options, masterSpaceId, config.selected_options]);

  const hasOptions = Object.keys(groupedOptions).length > 0;

  // Handle option selection
  const handleOptionChange = (group, optionName) => {
    const optionData = groupedOptions[group]?.find(opt => opt.name === optionName);
    onChange('selected_options', {
      ...config.selected_options,
      [group]: optionName,
    });
    // Also store the full option data for cost modifiers etc
    onChange('selected_options_data', {
      ...config.selected_options_data,
      [group]: optionData || null,
    });
  };

  return (
    <div className={styles.configForm}>
      {/* Item Options */}
      {hasOptions && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>אפשרויות</h3>
          {Object.entries(groupedOptions).map(([group, options]) => (
            <div key={group} className={styles.optionField}>
              <label className={styles.optionLabel}>
                {group === 'default' ? 'בחר אפשרות' : group}
              </label>
              <select
                className={styles.optionSelect}
                value={config.selected_options?.[group] || ''}
                onChange={e => handleOptionChange(group, e.target.value)}
              >
                <option value="">בחר...</option>
                {options.map(opt => (
                  <option key={opt.name} value={opt.name}>
                    {opt.name}
                    {opt.cost_modifier ? ` (+${formatCurrency(opt.cost_modifier)})` : ''}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      )}

      {/* Quantity and Cost */}
      <div className={styles.section}>
        <div className={styles.fieldRow}>
          {showQuantity && (
            <div className={styles.field}>
              <label className={styles.fieldLabel}>כמות</label>
              <input
                type="number"
                className={styles.fieldInput}
                min="1"
                value={config.quantity ?? 1}
                onChange={e => onChange('quantity', parseInt(e.target.value) || 1)}
              />
            </div>
          )}
          {priceExpanded ? (
            <div className={styles.field}>
              <label className={styles.fieldLabel}>מחיר</label>
              <input
                type="text"
                className={styles.fieldInput}
                value={config.cost_override || ''}
                onChange={e => {
                  const value = e.target.value.replace(/[^\d]/g, '');
                  onChange('cost_override', value ? parseInt(value) : null);
                }}
                placeholder={item.estimated_cost ? formatCurrency(item.estimated_cost) : ''}
              />
              {currentCostTypeName && (
                <div className={styles.costTypeBadge}>📏 {currentCostTypeName}</div>
              )}
            </div>
          ) : (
            <div className={styles.field}>
              <button
                type="button"
                className={styles.addPriceButton}
                onClick={() => setPriceExpanded(true)}
              >
                + הוספת הערכת מחיר
              </button>
            </div>
          )}
        </div>

        {/* VAT checkbox — only when price is expanded */}
        {showVat && priceExpanded && (
          <label className={styles.checkboxField}>
            <input
              type="checkbox"
              checked={config.incl_vat ?? true}
              onChange={e => onChange('incl_vat', e.target.checked)}
            />
            <span className={styles.checkboxLabel}>כולל מע&quot;מ</span>
          </label>
        )}
      </div>

      {/* Cost Type dropdown (only when item has no preset cost_type_id) */}
      {shouldShowCostType && (
        <div className={styles.section}>
          <div className={styles.optionField}>
            <label className={styles.optionLabel}>סוג עלות</label>
            <select
              className={styles.optionSelect}
              value={config.cost_type_id || ''}
              onChange={e => onChange('cost_type_id', e.target.value || null)}
            >
              <option value="">בחר סוג עלות</option>
              {costTypes.map(ct => (
                <option key={ct.id} value={ct.id}>
                  {ct.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

export default React.memo(ItemConfigForm);
